import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import "./Spinner.css";
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button } from "react-bootstrap";
import "./hm-theme.css";

const CreatePackages = ({ isOpen, onRequestClose, selectedPackage }) => {
  const navigate = useNavigate();

  const createUrl = "/api/package/create";
  const updateUrl = `/api/package/update`;

  const [admin, setAdmin] = useState({
    packagename: "",
    description: "",
    companyId: "",
    price: "",
    packageType: "",
    bookingCategoryIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Category-related state
  const [isCategoryEnabled, setIsCategoryEnabled] = useState(false);
  const [bookingCategories, setBookingCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);

  /**
   * Fetch company details to check if the category toggle is ON.
   * If ON, fetch booking categories for that company.
   */
  const fetchCompanyCategoryDetails = async (companyId) => {
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const compRes = await axios.get(`/api/companydetails/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const categoryEnabled = compRes.data?.packageByCategory === true;
      setIsCategoryEnabled(categoryEnabled);

      if (categoryEnabled) {
        const catRes = await axios.get(
          `/api/bookingcategory/companyId=${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBookingCategories(catRes.data || []);
      } else {
        setBookingCategories([]);
        setAdmin((prev) => ({
          ...prev,
          bookingCategoryIds: [],
          packageType: "",
        }));
      }
    } catch {
      setIsCategoryEnabled(false);
      setBookingCategories([]);
    }
  };

  // Pre-fill form when editing an existing package
  useEffect(() => {
    if (selectedPackage) {
      // Support both old single id and new array of ids
      let catIds = [];
      if (Array.isArray(selectedPackage.bookingCategoryIds)) {
        catIds = selectedPackage.bookingCategoryIds.map((id) => id.toString());
      } else if (selectedPackage.bookingCategoryId) {
        catIds = [selectedPackage.bookingCategoryId.toString()];
      }
      setAdmin({
        packagename: selectedPackage.packagename || "",
        description: selectedPackage.description || "",
        companyId: selectedPackage.companyId || "",
        price: selectedPackage.price || "",
        packageType: selectedPackage.packageType || "",
        bookingCategoryIds: catIds,
      });
      // Load category data for the pre-selected company in edit mode
      if (selectedPackage.companyId) {
        fetchCompanyCategoryDetails(selectedPackage.companyId);
      }
    }
  }, [selectedPackage]);

  // Load companies on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.getItem("data")).token;
        const companyRes = await axios.get("/api/companydetails/getCompanies", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanyDetails(companyRes.data || []);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = JSON.parse(localStorage.getItem("data")).token;
    const url = selectedPackage ? updateUrl : createUrl;

    if (admin.packagename && admin.companyId) {
      try {
        await axios({
          url,
          method: "POST",
          data: {
            id: selectedPackage ? selectedPackage.id : null,
            packagename: admin.packagename,
            description: admin.description,
            companyId: admin.companyId,
            price: admin.price,
            packageType: admin.packageType || null,
            bookingCategoryIds:
              admin.bookingCategoryIds.length > 0
                ? admin.bookingCategoryIds.map(Number)
                : null,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        setLoading(false);
        onRequestClose();
        setAdmin({
          packagename: "",
          description: "",
          companyId: "",
          price: "",
          packageType: "",
          bookingCategoryIds: [],
        });

        const action = selectedPackage ? "Updated" : "Added";
        toast.success(`Package ${action} Successfully`);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    } else {
      setLoading(false);
      toast.error("Please fill out all required fields.");
    }
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }

  function handleChange(e) {
    const { id, value } = e.target;
    if (id === "companyId") {
      const selected = companyDetails.find((c) => c.id.toString() === value);
      setSelectedCompany(selected);
      setAdmin((prevState) => ({
        ...prevState,
        companyId: value,
        packagename: `${selected?.companycode || ""}-`,
        bookingCategoryIds: [],
        packageType: "",
      }));
      // Reset category state when company changes
      setIsCategoryEnabled(false);
      setBookingCategories([]);
      if (value) {
        fetchCompanyCategoryDetails(value);
      }
    } else if (id === "packagename") {
      const companyCodeWithHyphen = selectedCompany?.companycode
        ? `${selectedCompany.companycode}-`
        : "";
      if (value.startsWith(companyCodeWithHyphen)) {
        setAdmin((prevState) => ({
          ...prevState,
          packagename: value,
        }));
      }
    } else {
      setAdmin((prevState) => ({
        ...prevState,
        [id]: value,
      }));
    }
  }

  function handleCategoryCheckbox(catId) {
    const idStr = catId.toString();
    setAdmin((prev) => {
      const already = prev.bookingCategoryIds.includes(idStr);
      return {
        ...prev,
        bookingCategoryIds: already
          ? prev.bookingCategoryIds.filter((id) => id !== idStr)
          : [...prev.bookingCategoryIds, idStr],
      };
    });
  }

  const handleKeyDown = (e) => {
    const { id, selectionStart, key } = e.target;
    const companyCodeWithHyphen = selectedCompany?.companycode
      ? `${selectedCompany.companycode}-`
      : "";
    if (
      id === "packagename" &&
      selectionStart <= companyCodeWithHyphen.length &&
      key === "Backspace"
    ) {
      e.preventDefault();
    }
  };

  return (
    <Modal show={isOpen} onHide={onRequestClose} centered>
      <Modal.Header closeButton className="hm-card-header">
        <Modal.Title className="hm-card-title">
          <i className="material-icons">inventory_2</i>
          {selectedPackage ? "Update Health Package" : "Add Health Package"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loading && <div className="loading"></div>}
        <form onSubmit={handleSubmit}>

          {/* Company */}
          <div className="mb-3">
            <label htmlFor="companyId" className="form-label small fw-semibold">
              Company <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              id="companyId"
              value={admin.companyId}
              onChange={handleChange}
              required
            >
              <option value="">Select Company</option>
              {companyDetails.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.companyname}
                </option>
              ))}
            </select>
          </div>

          {/* Package Name */}
          <div className="mb-3">
            <label
              htmlFor="packagename"
              className="form-label small fw-semibold"
            >
              Package Name <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              id="packagename"
              type="text"
              placeholder="e.g. COMP-PREMIUM-PEME"
              value={admin.packagename}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              required
            />
          </div>

          {/* Package Type — only visible when company has category toggle ON */}
          {isCategoryEnabled && (
            <div className="mb-3">
              <label
                htmlFor="packageType"
                className="form-label small fw-semibold"
              >
                Package Type
              </label>
              <select
                className="form-select"
                id="packageType"
                value={admin.packageType}
                onChange={handleChange}
              >
                <option value="">Select Package Type</option>
                <option value="Initial">Initial</option>
                <option value="Renewal">Renewal</option>
              </select>
            </div>
          )}

          {/* Package Price */}
          <div className="mb-3">
            <label
              htmlFor="price"
              className="form-label small fw-semibold"
            >
              Price (₹)
            </label>
            <input
              className="form-control"
              id="price"
              type="number"
              placeholder="e.g. 2500"
              value={admin.price}
              onChange={handleChange}
            />
          </div>

          {/* Booking Category — only visible when company has category toggle ON */}
          {isCategoryEnabled && (
            <div className="mb-3" style={{ position: "relative" }}>
              <label className="form-label small fw-semibold d-block">
                Booking Category
              </label>
              {bookingCategories.length > 0 ? (
                <>
                  {/* Dropdown trigger */}
                  <div
                    className="form-select d-flex align-items-center justify-content-between"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setCategoryDropdownOpen((prev) => !prev)}
                  >
                    <span className={admin.bookingCategoryIds.length === 0 ? "text-muted" : ""}>
                      {admin.bookingCategoryIds.length === 0
                        ? "Select Booking Category"
                        : admin.bookingCategoryIds.length === 1
                        ? bookingCategories.find(
                            (c) => c.id.toString() === admin.bookingCategoryIds[0]
                          )?.name ||
                          bookingCategories.find(
                            (c) => c.id.toString() === admin.bookingCategoryIds[0]
                          )?.categoryname
                        : `${admin.bookingCategoryIds.length} categories selected`}
                    </span>
                    <span style={{ fontSize: "10px", marginLeft: "8px" }}>
                      {categoryDropdownOpen ? "▲" : "▼"}
                    </span>
                  </div>

                  {/* Floating checkbox panel */}
                  {categoryDropdownOpen && (
                    <div
                      className="border rounded shadow-sm bg-white"
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        maxHeight: "200px",
                        overflowY: "auto",
                        padding: "8px 12px",
                        marginTop: "2px",
                      }}
                    >
                      {bookingCategories.map((cat) => {
                        const idStr = cat.id.toString();
                        const checked = admin.bookingCategoryIds.includes(idStr);
                        return (
                          <div
                            key={cat.id}
                            className="form-check py-1"
                            style={{ cursor: "pointer" }}
                          >
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`cat-${cat.id}`}
                              checked={checked}
                              onChange={() => handleCategoryCheckbox(cat.id)}
                            />
                            <label
                              className="form-check-label w-100"
                              htmlFor={`cat-${cat.id}`}
                              style={{ cursor: "pointer" }}
                            >
                              {cat.name || cat.categoryname}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="form-control bg-light text-muted small">
                  No booking categories found for this company.
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <label
              htmlFor="description"
              className="form-label small fw-semibold"
            >
              Description
            </label>
            <textarea
              className="form-control"
              id="description"
              placeholder="Package coverage, details..."
              value={admin.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              className="rounded-pill px-4"
              onClick={onRequestClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <button
              type="submit"
              className="btn-hm-primary"
              disabled={loading}
            >
              <i className="material-icons" style={{ fontSize: "1rem" }}>
                save
              </i>
              {selectedPackage ? "Update Package" : "Save Package"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CreatePackages;
